import { type ReactNode } from "react";
import { SortableTh } from "./ui.js";
import { useSortedRows, type SortValue } from "../lib/useSortedRows.js";

/**
 * One sortable table, declared as columns rather than wired up by hand.
 *
 * Every table on the site used to repeat the same three parallel lists — a map
 * of sort accessors, a `<SortableTh>` per column, and a `<td>` per column — which
 * had to be kept in step by hand and periodically weren't. A column now says
 * everything about itself in one place.
 *
 * A phone gets the same table rather than a stack of labelled lines per row:
 * these tables exist to be read down a column — who has the most points, who is
 * above whom — and a card per row throws that away. It scrolls sideways with
 * the column that names the row pinned to the left edge, which is how a box
 * score reads on a phone anywhere else.
 */
export interface Column<T> {
  /** stable id, also the sort key */
  key: string;
  label: ReactNode;
  /** what to show; defaults to the sort value */
  render?: (row: T) => ReactNode;
  /** what to sort on. Omit to make the column unsortable. */
  sortBy?: (row: T) => SortValue;
  align?: "left" | "right";
  /** tooltip on the header, for abbreviations like SOV */
  title?: string;
  /** keep this column when the table is asked to be compact */
  compact?: boolean;
  /** cell classes on top of the alignment defaults */
  className?: string;
  /** the column that names the row — pinned on a phone */
  primary?: boolean;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  initialSort: { key: string; dir: "asc" | "desc" };
  /** drop every column not marked `compact` — the narrow sidebar variant */
  compact?: boolean;
  /** minimum width before the table scrolls sideways on a tablet */
  minWidth?: number;
  /** rendered under the table, inside the same bordered box */
  footnote?: ReactNode;
  empty?: ReactNode;
  rowClassName?: (row: T) => string;
}

export default function DataTable<T>({
  rows,
  columns,
  rowKey,
  initialSort,
  compact = false,
  minWidth,
  footnote,
  empty,
  rowClassName,
}: DataTableProps<T>) {
  const shown = compact ? columns.filter((c) => c.compact) : columns;

  // One cell is pinned, not several: a second pinned cell would need the
  // first one's exact width as its offset, and would slide under it the moment
  // a name ran long.
  //
  // A single narrow marker in front of the name rides along inside it — a
  // standings row is "3 Adam", not a rank floating away from a name. Two or
  // more columns in front of it are context rather than identity (the game log
  // opens with a season and a stage), and context scrolls with everything else,
  // or the pinned cell is the whole screen.
  const primaryIndex = shown.findIndex((c) => c.primary);
  const nameIndex = primaryIndex >= 0 ? primaryIndex : 0;
  const ridesAlong = nameIndex === 1 ? shown.slice(0, 1) : [];
  const leadColumn = shown[nameIndex];
  const rest = shown.filter((c, i) => i !== nameIndex && !ridesAlong.includes(c));
  // Tapping the pinned header sorts by the leftmost thing in it, which for the
  // standings is the rank — the order the table is meant to be read in.
  const leadSort = [...ridesAlong, leadColumn].find((c) => c?.sortBy);

  const accessors: Record<string, (row: T) => SortValue> = {};
  for (const c of shown) {
    if (c.sortBy) accessors[c.key] = c.sortBy;
  }

  const { sorted, sort, onSort } = useSortedRows(rows, accessors, initialSort);

  const cellOf = (c: Column<T>, row: T): ReactNode =>
    c.render ? c.render(row) : (c.sortBy?.(row) ?? "—");

  const headerCell = (c: Column<T>, className = "") =>
    c.sortBy ? (
      <SortableTh
        key={c.key}
        sortKey={c.key}
        sort={sort}
        onSort={onSort}
        align={c.align}
        title={c.title}
        className={className}
      >
        {c.label}
      </SortableTh>
    ) : (
      <th
        key={c.key}
        className={`th ${c.align === "right" ? "text-right" : ""} ${className}`}
        title={c.title}
      >
        {c.label}
      </th>
    );

  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <>
      {/* Tablet and up: every column, in the order the table declared them. */}
      <div className={compact ? "overflow-x-auto" : "scroll-x"}>
        <table
          className="hidden w-full sm:table"
          style={minWidth && !compact ? { minWidth } : undefined}
        >
          <thead>
            <tr className="border-b border-field-700">{shown.map((c) => headerCell(c))}</tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={rowKey(row)}
                className={`border-b border-field-800 last:border-b-0 hover:bg-field-850/50 ${
                  rowClassName?.(row) ?? ""
                }`}
              >
                {shown.map((c) => (
                  <td
                    key={c.key}
                    className={`td ${c.align === "right" ? "text-right" : ""} ${c.className ?? ""}`}
                  >
                    {cellOf(c, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone: the same table, tighter, with the name column pinned. A table
          narrower than the screen still fills it (`min-w-full`); a wide one
          scrolls, and the name stays put. */}
      <div className="overflow-x-auto sm:hidden">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-field-700">
              {leadColumn &&
                (leadSort ? (
                  <SortableTh
                    sortKey={leadSort.key}
                    sort={sort}
                    onSort={onSort}
                    title={leadColumn.title}
                    className="pin-col whitespace-nowrap px-2"
                  >
                    {leadColumn.label}
                  </SortableTh>
                ) : (
                  <th className="th pin-col whitespace-nowrap px-2" title={leadColumn.title}>
                    {leadColumn.label}
                  </th>
                ))}
              {rest.map((c) => headerCell(c, "whitespace-nowrap px-2"))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={rowKey(row)}
                className={`border-b border-field-800 last:border-b-0 ${
                  rowClassName?.(row) ?? ""
                }`}
              >
                {leadColumn && (
                  <td className="pin-col whitespace-nowrap px-2 py-2 text-sm">
                    <span className="flex items-baseline gap-1.5">
                      {ridesAlong.map((c) => (
                        <span key={c.key} className={`text-xs ${c.className ?? ""}`}>
                          {cellOf(c, row)}
                        </span>
                      ))}
                      <span className={leadColumn.className ?? ""}>
                        {cellOf(leadColumn, row)}
                      </span>
                    </span>
                  </td>
                )}
                {rest.map((c) => (
                  <td
                    key={c.key}
                    className={`td px-2 ${c.align === "right" ? "text-right" : ""} ${
                      c.className ?? ""
                    }`}
                  >
                    {cellOf(c, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* A div, not a p: several tables pass a footnote with its own
          paragraphs in it, and a <p> inside a <p> is invalid — the browser
          closes the outer one early and the layout comes apart. */}
      {footnote && (
        <div className="border-t border-field-800 px-4 py-3 text-xs text-chalk-500">
          {footnote}
        </div>
      )}
    </>
  );
}
